import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Bell, MessageSquare, Mail, Send, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export function SendReminderDialog({ open, onOpenChange, order, client }) {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [reminderType, setReminderType] = useState('payment');
  const [channel, setChannel] = useState('sms');
  const [customMessage, setCustomMessage] = useState('');
  const [result, setResult] = useState(null);

  const handleSend = async () => {
    setLoading(true);
    setResult(null);
    
    try {
      const res = await api.post('/reminders/send', {
        client_id: client?.id,
        order_id: order?.id,
        reminder_type: reminderType,
        channel: channel,
        custom_message: reminderType === 'custom' ? customMessage : null
      });
      
      setResult(res.data);
      
      if (res.data.status === 'sent') {
        toast.success(t('reminders.sent'));
      } else {
        toast.error(t('reminders.failed'));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('reminders.failed'));
      setResult({ status: 'failed', error: err.response?.data?.detail });
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setResult(null);
    setCustomMessage('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={resetAndClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            {t('reminders.sendReminder')}
          </DialogTitle>
          <DialogDescription>
            {client?.name} • {client?.phone || client?.email || 'No contact'}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="py-6">
            <div className={cn(
              "flex flex-col items-center gap-3 p-6 rounded-lg",
              result.status === 'sent' ? "bg-green-50 dark:bg-green-900/20" : "bg-red-50 dark:bg-red-900/20"
            )}>
              {result.status === 'sent' ? (
                <>
                  <CheckCircle className="h-12 w-12 text-green-500" />
                  <p className="font-medium text-green-700 dark:text-green-400">{t('reminders.sentSuccess')}</p>
                  {result.sms_result?.success && (
                    <p className="text-sm text-muted-foreground">SMS: ✓</p>
                  )}
                  {result.email_result?.success && (
                    <p className="text-sm text-muted-foreground">Email: ✓</p>
                  )}
                </>
              ) : (
                <>
                  <XCircle className="h-12 w-12 text-red-500" />
                  <p className="font-medium text-red-700 dark:text-red-400">{t('reminders.sentFailed')}</p>
                  {result.sms_result?.message && (
                    <p className="text-xs text-muted-foreground">SMS: {result.sms_result.message}</p>
                  )}
                  {result.email_result?.message && (
                    <p className="text-xs text-muted-foreground">Email: {result.email_result.message}</p>
                  )}
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('reminders.type')}</Label>
              <Select value={reminderType} onValueChange={setReminderType}>
                <SelectTrigger data-testid="reminder-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="payment">{t('reminders.type.payment')}</SelectItem>
                  <SelectItem value="appointment">{t('reminders.type.appointment')}</SelectItem>
                  <SelectItem value="custom">{t('reminders.type.custom')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('reminders.channel')}</Label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger data-testid="reminder-channel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sms" disabled={!client?.phone}>
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      SMS {!client?.phone && `(${t('reminders.noPhone')})`}
                    </div>
                  </SelectItem>
                  <SelectItem value="email" disabled={!client?.email}>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Email {!client?.email && `(${t('reminders.noEmail')})`}
                    </div>
                  </SelectItem>
                  <SelectItem value="both" disabled={!client?.phone || !client?.email}>
                    <div className="flex items-center gap-2">
                      <Bell className="h-4 w-4" />
                      {t('reminders.both')}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {reminderType === 'custom' && (
              <div className="space-y-2">
                <Label>{t('reminders.customMessage')}</Label>
                <Textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder={t('reminders.customPlaceholder')}
                  rows={3}
                  data-testid="reminder-message"
                />
                <p className="text-xs text-muted-foreground">
                  {customMessage.length}/160 {t('reminders.characters')}
                </p>
              </div>
            )}

            {reminderType === 'payment' && order && (
              <div className="p-3 rounded-lg bg-muted/50 text-sm">
                <p className="text-muted-foreground">{t('reminders.preview')}:</p>
                <p className="mt-1">
                  Hi {client?.name}! This is a friendly reminder. Your order "{order.title}" has an outstanding balance of ${order.total_amount?.toFixed(2) || '0.00'}...
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={resetAndClose}>
            {result ? t('common.close') : t('common.cancel')}
          </Button>
          {!result && (
            <Button onClick={handleSend} disabled={loading || (reminderType === 'custom' && !customMessage)}>
              {loading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              {t('reminders.send')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReminderButton({ order, client, size = "default" }) {
  const { t } = useLanguage();
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!client) return null;

  return (
    <>
      <Button 
        variant="outline" 
        size={size}
        onClick={() => setDialogOpen(true)}
        data-testid="send-reminder-btn"
      >
        <Bell className="h-4 w-4 mr-2" />
        {t('reminders.remind')}
      </Button>
      
      <SendReminderDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        order={order}
        client={client}
      />
    </>
  );
}
